// Listens to the microphone and reports tempo and beat timing about twice a
// second. Analysis is triggered by audio-thread messages rather than timers,
// so it keeps running when the tab is in the background.

import { audioTimeToEpochMs } from "./audio-clock.ts";
import { HOP_SIZE, type OnsetFrame } from "./onset.ts";
import { estimateTempo, type TempoEstimate } from "./tempo.ts";

const WINDOW_SECONDS = 8;
const ANALYZE_EVERY_SECONDS = 0.5;

export interface BeatReading {
  estimate: TempoEstimate | null;
  // Epoch ms of the most recent detected beat (null without an estimate).
  lastBeatEpochMs: number | null;
  // Average RMS over the last second, 0-1.
  level: number;
}

export class BeatListener {
  private readonly onReading: (reading: BeatReading) => void;
  private ctx: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private frames: OnsetFrame[] = [];
  private sinceAnalysis = 0;

  constructor(onReading: (reading: BeatReading) => void) {
    this.onReading = onReading;
  }

  async start(): Promise<void> {
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error("The microphone only works on http://localhost or an https page.");
    }
    // Raw audio: echo cancellation and noise suppression flatten drums.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
    });
    const ctx = new AudioContext();
    this.ctx = ctx;
    await ctx.audioWorklet.addModule(new URL("onset-worklet.js", import.meta.url));
    const source = ctx.createMediaStreamSource(this.stream);
    const node = new AudioWorkletNode(ctx, "onset", {
      channelCount: 1,
      channelCountMode: "explicit",
    });
    // Silent path to the output so the graph is guaranteed to run.
    const mute = ctx.createGain();
    mute.gain.value = 0;
    source.connect(node).connect(mute).connect(ctx.destination);
    node.port.onmessage = (event: MessageEvent<OnsetFrame[]>) => this.receive(event.data);
  }

  stop(): void {
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    void this.ctx?.close();
    this.ctx = null;
    this.frames = [];
  }

  private receive(batch: OnsetFrame[]): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.frames.push(...batch);
    const frameRate = ctx.sampleRate / HOP_SIZE;
    const keep = Math.round(WINDOW_SECONDS * frameRate);
    if (this.frames.length > keep) this.frames.splice(0, this.frames.length - keep);

    this.sinceAnalysis += batch.length;
    if (this.sinceAnalysis < ANALYZE_EVERY_SECONDS * frameRate) return;
    this.sinceAnalysis = 0;

    const last = this.frames[this.frames.length - 1];
    if (!last) return;
    const estimate = estimateTempo(
      this.frames.map((f) => f.flux),
      frameRate,
      last.time,
    );
    const recent = this.frames.slice(-Math.round(frameRate));
    const level = recent.reduce((sum, f) => sum + f.rms, 0) / Math.max(1, recent.length);
    this.onReading({
      estimate,
      lastBeatEpochMs: estimate ? audioTimeToEpochMs(ctx, estimate.lastBeatTime) : null,
      level,
    });
  }
}
